import React, { useState } from 'react';

interface CustomTooltipProps {
  title: string;
  children: React.ReactElement;
  arrow?: boolean;
  placement?: 'top' | 'right'; // Supporting simplified placements
  xOffset?: number;
  yOffset?: number;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  title,
  children,
  placement = 'top',
  xOffset = 0,
  yOffset = 10,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);

  // Styling based on placement
  const tooltipStyle: React.CSSProperties = {
    position: 'absolute',
    backgroundColor: 'rgba(97, 97, 97, 0.92)',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.6875rem',
    whiteSpace: 'nowrap',
    zIndex: 1500,
    pointerEvents: 'none',
    // Common transitions
    transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isVisible ? 1 : 0,
    visibility: isVisible ? 'visible' : 'hidden',
  };

  // Calculate position
  // We use a simplified logic: 
  // Top: bottom: 100% + yOffset, left: 50% + xOffset, transform: translateX(-50%)
  // Right: left: 100% + yOffset, top: 50% + xOffset, transform: translateY(-50%)

  if (placement === 'top') {
    Object.assign(tooltipStyle, {
      bottom: `calc(100% + ${yOffset}px)`,
      left: `calc(50% + ${xOffset}px)`,
      transform: 'translateX(-50%)',
    });
  } else if (placement === 'right') {
    Object.assign(tooltipStyle, {
      left: `calc(100% + ${yOffset}px)`, // yOffset acts as distance from element
      top: `calc(50% + ${xOffset}px)`,   // xOffset acts as vertical shift
      transform: 'translateY(-50%)',
    });
  }

  // Arrow Style
  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  };

  if (placement === 'top') {
    Object.assign(arrowStyle, {
      top: '100%',
      left: '50%',
      marginLeft: '-5px',
      borderWidth: '5px 5px 0 5px',
      borderColor: 'rgba(97, 97, 97, 0.92) transparent transparent transparent',
    });
  } else if (placement === 'right') {
    Object.assign(arrowStyle, {
      right: '100%',
      top: '50%',
      marginTop: '-5px',
      borderWidth: '5px 5px 5px 0',
      borderColor: 'transparent rgba(97, 97, 97, 0.92) transparent transparent',
    });
  }

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <div style={tooltipStyle}>
        {title}
        <div style={arrowStyle} />
      </div>
    </div>
  );
};

export default CustomTooltip;
